import { AxiosResponse } from "axios";
import { Action } from "redux";
import {
  Picture,
  PictureGroup,
  ThunkAction,
  ThunkDispatch,
} from "../types/index.js";
import { fetch } from "../utils/utils.js";
import { showAlert } from "./Alerts.js";
import { isActionType } from "./utils.js";

const ActionTypeKeys = [
  "GET_ALL_MEDIA_SUCCESS",
  "GET_ALL_MEDIA_ERROR",
  "GET_ALL_MEDIA_START",
  "GET_ALL_MEDIA_GROUPS_SUCCESS",
  "GET_ALL_MEDIA_GROUPS_ERROR",
  "GET_ALL_MEDIA_GROUPS_START",
  "GET_MEDIA_GROUP_SUCCESS",
  "GET_MEDIA_GROUP_ERROR",
  "GET_MEDIA_GROUP_START",
  "UPLOAD_MEDIA_START",
  "UPLOAD_MEDIA_ERROR",
  "UPLOAD_MEDIA_SUCCESS",
  "DELETE_MEDIA_START",
  "DELETE_MEDIA_SUCCESS",
  "DELETE_MEDIA_ERROR",
] as const;

type ActionType = (typeof ActionTypeKeys)[number];
type ActionBase<T extends ActionType> = Action<T>;

type GetAllMediaSuccess = ActionBase<"GET_ALL_MEDIA_SUCCESS"> & {
  pictures: Picture[];
  groupId?: number;
};
type GetAllMediaError = ActionBase<"GET_ALL_MEDIA_ERROR"> & { error: Error };
type GetAllMediaStart = ActionBase<"GET_ALL_MEDIA_START">;
type GetAllMediaGroupsSuccess = ActionBase<"GET_ALL_MEDIA_GROUPS_SUCCESS"> & {
  groups: PictureGroup[];
};
type GetAllMediaGroupsError = ActionBase<"GET_ALL_MEDIA_GROUPS_ERROR"> & {
  error: Error;
};
type GetAllMediaGroupsStart = ActionBase<"GET_ALL_MEDIA_GROUPS_START">;
type GetMediaGroupSuccess = ActionBase<"GET_MEDIA_GROUP_SUCCESS"> & {
  pictures: Picture[];
  groupId: number;
};
type GetMediaGroupError = ActionBase<"GET_MEDIA_GROUP_ERROR"> & {
  error: Error;
};
type GetMediaGroupStart = ActionBase<"GET_MEDIA_GROUP_START"> & {
  groupId: number;
};
type UploadMediaStart = ActionBase<"UPLOAD_MEDIA_START">;
type UploadMediaError = ActionBase<"UPLOAD_MEDIA_ERROR"> & { error: Error };
type UploadMediaSuccess = ActionBase<"UPLOAD_MEDIA_SUCCESS"> & {
  picture: Picture;
};
type DeleteMediaStart = ActionBase<"DELETE_MEDIA_START">;
type DeleteMediaSuccess = ActionBase<"DELETE_MEDIA_SUCCESS"> & { id: string };
type DeleteMediaError = ActionBase<"DELETE_MEDIA_ERROR"> & { error: Error };

export type MediaLibraryAction =
  | GetAllMediaSuccess
  | GetAllMediaError
  | GetAllMediaStart
  | GetAllMediaGroupsSuccess
  | GetAllMediaGroupsError
  | GetAllMediaGroupsStart
  | GetMediaGroupSuccess
  | GetMediaGroupError
  | GetMediaGroupStart
  | UploadMediaStart
  | UploadMediaError
  | UploadMediaSuccess
  | DeleteMediaStart
  | DeleteMediaSuccess
  | DeleteMediaError;

export const isMediaLibraryAction = (
  action: Action,
): action is MediaLibraryAction => isActionType(action, ActionTypeKeys);

const fetchMedia = async ({
  siteId,
  dispatch,
  groupId,
}: {
  siteId: string;
  dispatch: ThunkDispatch;
  groupId?: number;
}): Promise<Picture[]> => {
  const { data } = await (<Promise<AxiosResponse<Picture[]>>>(
    fetch({ dispatch }).get(`sites/${siteId}/media`, { params: { groupId } })
  ));

  return data;
};

export const getAllMedia =
  (siteId: string): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    if (getState().loadStates.mediaLibrary.pictures !== "unloaded") {
      return;
    }

    dispatch(getAllMediaStart());
    try {
      const data = await fetchMedia({ siteId, dispatch });
      dispatch(getAllMediaSuccess(data));
    } catch (error) {
      error instanceof Error && dispatch(getAllMediaError(error));
      throw error;
    }
  };

export const getMediaGroup =
  (siteId: string, groupId: number): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const loadStatus =
      getState().loadStates.mediaLibrary.picturesByGroupId[groupId];

    if (loadStatus !== "unloaded" && loadStatus !== undefined) {
      return;
    }

    dispatch(getMediaGroupStart(groupId));
    try {
      const data = await fetchMedia({ siteId, dispatch, groupId });
      dispatch(getMediaGroupSuccess(data, groupId));
    } catch (error) {
      error instanceof Error && dispatch(getMediaGroupError(error));
      throw error;
    }
  };

export const getAllMediaGroups =
  (siteId: string): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    if (getState().loadStates.mediaLibrary.groups !== "unloaded") {
      return;
    }

    dispatch(getMediaGroupsStart());
    try {
      const { data } = await (<Promise<AxiosResponse<PictureGroup[]>>>(
        fetch({ dispatch }).get(`sites/${siteId}/media/groups`)
      ));

      dispatch(getMediaGroupsSuccess(data));
    } catch (error) {
      error instanceof Error && dispatch(getMediaGroupsError(error));
      dispatch(
        showAlert("Beim Laden der Bildergruppen ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  };

export const getAllMediaSuccess = (
  pictures: Picture[],
): GetAllMediaSuccess => ({ type: "GET_ALL_MEDIA_SUCCESS", pictures });

const getAllMediaError = (error: Error): GetAllMediaError => ({
  type: "GET_ALL_MEDIA_ERROR",
  error,
});

const getAllMediaStart = (): GetAllMediaStart => ({
  type: "GET_ALL_MEDIA_START",
});

const getMediaGroupsSuccess = (
  groups: PictureGroup[],
): GetAllMediaGroupsSuccess => ({
  type: "GET_ALL_MEDIA_GROUPS_SUCCESS",
  groups,
});

const getMediaGroupsError = (error: Error): GetAllMediaGroupsError => ({
  type: "GET_ALL_MEDIA_GROUPS_ERROR",
  error,
});

const getMediaGroupsStart = (): GetAllMediaGroupsStart => ({
  type: "GET_ALL_MEDIA_GROUPS_START",
});

const getMediaGroupSuccess = (
  pictures: Picture[],
  groupId: number,
): GetMediaGroupSuccess => ({
  type: "GET_MEDIA_GROUP_SUCCESS",
  pictures,
  groupId,
});

const getMediaGroupError = (error: Error): GetMediaGroupError => ({
  type: "GET_MEDIA_GROUP_ERROR",
  error,
});

const getMediaGroupStart = (groupId: number): GetMediaGroupStart => ({
  type: "GET_MEDIA_GROUP_START",
  groupId,
});

export const uploadMedia =
  (siteId: string, file: File): ThunkAction<Promise<void>> =>
  async (dispatch) => {
    const formData = new FormData();
    formData.append("file", file);
    dispatch<UploadMediaStart>({ type: "UPLOAD_MEDIA_START" });

    try {
      const { data } = await fetch({ dispatch }).post<Picture>(
        `sites/${siteId}/logos`,
        formData,
      );
      dispatch<UploadMediaSuccess>({
        type: "UPLOAD_MEDIA_SUCCESS",
        picture: data,
      });
    } catch (error) {
      error instanceof Error &&
        dispatch<UploadMediaError>({ type: "UPLOAD_MEDIA_ERROR", error });
      dispatch(
        showAlert("Beim Hochladen der Datei ist ein Fehler aufgetreten"),
      );
      throw error;
    }
  };

export const deleteMedia =
  (siteId: string, id: string): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch<DeleteMediaStart>({ type: "DELETE_MEDIA_START" });

    const state = getState();
    const picture = state.mediaLibrary.pictures[id];
    if (!picture) {
      dispatch<DeleteMediaSuccess>({ type: "DELETE_MEDIA_SUCCESS", id });
      return;
    }

    try {
      const pathParts = picture.url.split("/");
      const filename = pathParts[pathParts.length - 1];

      await fetch({ dispatch }).delete(`sites/${siteId}/logos/${filename}`);
      dispatch<DeleteMediaSuccess>({ type: "DELETE_MEDIA_SUCCESS", id });
    } catch (error) {
      error instanceof Error &&
        dispatch<DeleteMediaError>({ type: "DELETE_MEDIA_ERROR", error });
      dispatch(
        showAlert("Beim Hochladen der Datei ist ein Fehler aufgetreten"),
      );
      throw error;
    }
  };
